import { useCallback, useEffect, useRef, useState } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { SPRING, clampWithRubberband, snapIndexFor } from "../lib/motion";
import "./drag-carousel.css";

/** Velocity from the last ~100ms of pointer samples, not a single frame delta. */
const VELOCITY_WINDOW_MS = 100;
/** Movement before a drag commits, so a tap on a card is still a tap. */
const DRAG_THRESHOLD_PX = 10;

export default function DragCarousel({ items, renderItem, getKey, label }) {
  const reduce = useReducedMotion();
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const x = useMotionValue(0);

  const [index, setIndex] = useState(0);
  const [step, setStep] = useState(0);

  const drag = useRef({
    active: false,
    committed: false,
    startX: 0,
    startOffset: 0,
    history: [],
  });

  /*
   * The step is read from the DOM rather than assumed, because the card width is
   * set in CSS and changes at every breakpoint.
   */
  useEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track || track.children.length < 1) return;
      const first = track.children[0].getBoundingClientRect();
      const second = track.children[1]?.getBoundingClientRect();
      setStep(second ? second.left - first.left : first.width);
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [items.length]);

  const goTo = useCallback(
    (nextIndex, velocity = 0) => {
      const clamped = Math.min(items.length - 1, Math.max(0, nextIndex));
      setIndex(clamped);

      const target = -clamped * step;
      if (reduce) {
        x.set(target);
        return;
      }

      /*
       * Animating from the motion value's current position, with the release
       * velocity handed straight to the spring, is what removes the seam
       * between dragging and animating.
       */
      animate(x, target, { ...SPRING.momentum, velocity });
    },
    [items.length, reduce, step, x]
  );

  // Keep the track aligned when the step changes under a resize.
  useEffect(() => {
    if (step > 0) x.set(-index * step);
  }, [step, index, x]);

  const onPointerDown = (e) => {
    if (reduce || step === 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      active: true,
      committed: false,
      startX: e.clientX,
      startOffset: x.get(),
      history: [{ x: e.clientX, t: e.timeStamp }],
    };
  };

  const onPointerMove = (e) => {
    const state = drag.current;
    if (!state.active) return;

    const dx = e.clientX - state.startX;
    if (!state.committed) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      state.committed = true;
    }

    state.history.push({ x: e.clientX, t: e.timeStamp });
    if (state.history.length > 6) state.history.shift();

    const min = -(items.length - 1) * step;
    // Past either end it resists progressively. A hard stop reads as frozen.
    x.set(clampWithRubberband(state.startOffset + dx, min, 0, step));
  };

  const endDrag = (e) => {
    const state = drag.current;
    if (!state.active) return;
    state.active = false;

    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (!state.committed) return;

    const history = state.history;
    const last = history[history.length - 1];
    const first =
      history.find((sample) => last.t - sample.t <= VELOCITY_WINDOW_MS) ||
      history[0];
    const dt = last.t - first.t;
    const velocity = dt > 0 ? ((last.x - first.x) / dt) * 1000 : 0;

    goTo(
      snapIndexFor({ offset: x.get(), velocity, step, count: items.length }),
      velocity
    );
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goTo(index + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goTo(index - 1);
    }
  };

  if (reduce) {
    return (
      <div className="carousel">
        <div
          className="carousel__viewport carousel__viewport--snap"
          role="group"
          aria-roledescription="carousel"
          aria-label={label}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          <div className="carousel__track carousel__track--static">
            {items.map((item, i) => (
              <div className="carousel__cell" key={getKey(item)}>
                {renderItem(item, i)}
              </div>
            ))}
          </div>
        </div>
        <Dots count={items.length} index={index} onSelect={setIndex} />
      </div>
    );
  }

  return (
    <div className="carousel">
      <div
        className="carousel__viewport"
        ref={viewportRef}
        role="group"
        aria-roledescription="carousel"
        aria-label={label}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <motion.div className="carousel__track" ref={trackRef} style={{ x }}>
          {items.map((item, i) => (
            <div className="carousel__cell" key={getKey(item)}>
              {renderItem(item, i)}
            </div>
          ))}
        </motion.div>
      </div>
      <Dots count={items.length} index={index} onSelect={(i) => goTo(i)} />
    </div>
  );
}

function Dots({ count, index, onSelect }) {
  return (
    <div className="carousel__dots">
      {Array.from({ length: count }, (_, i) => (
        <button
          key={i}
          className="carousel__dot"
          type="button"
          aria-label={`Go to slide ${i + 1}`}
          aria-current={i === index}
          onClick={() => onSelect(i)}
        />
      ))}
    </div>
  );
}
